import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../components/inputs/FractionInput";

export const ORANGE = "#f59e0b";

export function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function digitsOf(value) {
  return String(value).split("");
}

export function padDigits(value, cols) {
  return String(value).padStart(cols, " ").split("");
}

export function createNumericInputTask({ type, pickData, renderTaskText, explain }) {
  function generateTask() {
    return {
      type,
      data: pickData(),
      tags: ["liczby_naturalne", "dzialania_pisemne"],
    };
  }

  function renderTask({ data }) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>{renderTaskText(data)}</p>
      </div>
    );
  }

  function renderInput(_task, userAnswer, onChange, disabled) {
    return (
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="wynik"
        placeholder="np. 1234"
      />
    );
  }

  function checkAnswer({ data }, userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return false;
    return userAnswer.parsed === data.result;
  }

  function renderAnswer({ data }) {
    return <InlineMath math={`${data.result}`} />;
  }

  function renderUserAnswer(userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
      return <i>(brak odpowiedzi)</i>;
    }
    return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
  }

  function renderExplanation({ data }) {
    return explain(data);
  }

  return {
    generateTask,
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  };
}

export function DigitRow({
  sign,
  digits,
  highlightIndices = [],
  mutedIndices = [],
  strikeIndices = [],
  strikeCurrentIndices = strikeIndices,
  small = false,
  muted = false,
}) {
  const digitTrack = "1.08ch";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `1ch repeat(${digits.length}, ${digitTrack})`,
        columnGap: "0.18em",
        alignItems: "end",
        whiteSpace: "nowrap",
      }}
    >
      <span>{sign || "\u00A0"}</span>
      {digits.map((digit, idx) => {
        const highlighted = highlightIndices.includes(idx);
        const indexMuted = mutedIndices.includes(idx);
        const struck = strikeIndices.includes(idx);
        const currentStrike = strikeCurrentIndices.includes(idx);
        return (
          <span
            key={idx}
            style={{
              minWidth: digitTrack,
              textAlign: "center",
              fontSize: small ? "0.72em" : "1em",
              ...(small && String(digit).length > 1
                ? {
                    fontSize: "0.56em",
                    letterSpacing: 0,
                  }
                : null),
              color: highlighted
                ? ORANGE
                : muted || indexMuted
                  ? "var(--color-muted, #94a3b8)"
                  : undefined,
              fontWeight: highlighted ? 700 : undefined,
              textDecorationLine: struck ? "line-through" : undefined,
              textDecorationThickness: struck ? "2px" : undefined,
              textDecorationColor: struck
                ? currentStrike
                  ? ORANGE
                  : "var(--color-text)"
                : undefined,
            }}
          >
            {digit === " " || digit === "" ? "\u00A0" : digit}
          </span>
        );
      })}
    </div>
  );
}

export function WrittenBoard({ children }) {
  return (
    <div
      style={{
        padding: "10px 14px",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        background: "transparent",
        width: "fit-content",
      }}
    >
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          lineHeight: 1.2,
          display: "inline-block",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function StepStrip({ steps, alignItems = "center" }) {
  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 10,
        padding: 10,
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        background: "transparent",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems, gap: 8 }}>
        {steps.map((step, idx) => (
          <React.Fragment key={idx}>
            {step}
            {idx < steps.length - 1 ? (
              <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>{"\u2192"}</div>
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
